/**
 * User-facing message for a failed playground generation.
 *
 * The backend already maps provider errors to short sentences, but older
 * deployments (and any path that forwards a raw provider error) can still hand
 * us the full provider JSON ("Error code: 429 - {'error': {...}}"), which can
 * echo request details. This never shows that: anything that looks like a raw
 * dump is replaced with a short friendly sentence.
 */
const FALLBACK = 'Failed to generate preview. Please try again in a moment.'

const RULES: Array<[RegExp, string]> = [
  [/insufficient_quota|exceeded your current quota|credit balance|billing|quota|out of credits/i,
    'The AI provider account is out of credits or quota. Please top up the provider account or try again later.'],
  [/rate.?limit|\b429\b|too many requests|overloaded|\b529\b/i,
    'The AI provider is busy or rate limited right now. Please try again in a moment.'],
  [/invalid.{0,20}api.?key|incorrect api key|authentication|unauthorized|\b401\b|\b403\b/i,
    'The AI provider rejected the API key. Please check your key in Settings.'],
  [/model_not_found|does not exist|do not have access/i,
    'The selected AI model is not available for this account. Please pick another model.'],
  [/timeout|timed out|\b50[0234]\b|server error|unavailable/i,
    'The AI provider is temporarily unavailable. Please try again shortly.'],
]

const LOOKS_RAW = /[{}]|sk-[A-Za-z0-9_-]{6,}|error code:|traceback|\bat 0x/i
const MAX_SAFE_LENGTH = 200

export function playgroundErrorMessage(error: unknown): string {
  const failure = error as { response?: { data?: { detail?: unknown } }; message?: unknown }
  const detail = failure?.response?.data?.detail
  // A failed background job (deck preview) has no HTTP response: its reason is
  // the Error message. With a response, the message is axios boilerplate
  // ("Request failed with status code 500"), so only `detail` counts.
  const raw = typeof detail === 'string'
    ? detail
    : !failure?.response && typeof failure?.message === 'string' ? failure.message : ''
  if (!raw) return FALLBACK
  // Short, clean text (the backend's own sentences) is shown as is.
  if (!LOOKS_RAW.test(raw) && raw.length <= MAX_SAFE_LENGTH) return raw
  for (const [pattern, message] of RULES) {
    if (pattern.test(raw)) return message
  }
  return FALLBACK
}
